// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 *
 * <HeaderClickable
 *      iconSrc={icon}
 *      onClick={() => !isMobile && history.push(cartUrl ?? "")}
 *      iconProps={{ type: "miniCart" }}
 *      clickableProps={{ "data-test-selector": "cartLink" }}
 *      titleProps={{
 *          "data-test-selector": totalCountDisplay ? "cartLinkQuantity" : "cartLinkSubTotal",
 *      }}
 *      >
 *          <>
 *              {`${translate("Top Message")}`}
 *              <Typography className="sub-title">{`${translate("Bottom Message")}`}</Typography>
 *          </>
 *      </HeaderClickable>
 */

import { CustomAppState } from "@flip/App/Store/Reducers";
import AngleDown from "@flip/XDL/Icons/AngleDown";
import { headingsFontConfig } from "@flip/XDL/PreStyle/typography";
import { color, ellipsis, hideElement, mergeWithCss, xScreen } from "@flip/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable/Clickable";
import Icon, { IconPresentationProps, IconProps, IconWrapper } from "@insite/mobius/Icon";
import { StyledIcon } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, MouseEventHandler } from "react";
import { useState } from "react";
import { css } from "styled-components";

interface HeaderClickableProps {
    onClick?: MouseEventHandler<any>;
    iconProps?: IconProps;
    titleProps?: TypographyProps;
    title?: string;
    titleBreakWords?: boolean;
    extendedStyles?: HeaderClickableStyle;
    isNewTabletScreen?: boolean;
}

type Props = HeaderClickableProps & ClickableProps;

export interface HeaderClickableStyle {
    clickable?: ClickablePresentationProps;
    icon?: IconPresentationProps;
    title?: TypographyProps;
    cartItem?: {
        wrapper?: InjectableCss;
        text?: TypographyProps;
    };
    arrowIcon?: IconProps;
}

const headerClickableStyles: HeaderClickableStyle = {
    clickable: {
        css: css`
            &.mobile-mode {
                display: flex;
                justify-content: center;
                align-items: center;
            }

            &:focus {
                outline: none;
            }

            .arrow {
                transition: transform ease-in-out 250ms;
                color: ${color("primary")};

                ${xScreen("lte", 1150)} {
                    display: none;
                }
            }

            &[aria-expanded="true"] {
                > .clickable-title {
                    color: ${color("common.ui13")};
                }

                .arrow {
                    transform: rotate(180deg);
                    color: ${color("secondary")};
                }
            }
        `,
    },
    icon: {
        css: css`
            margin-right: 8px;

            ${xScreen("lte", "md")} {
                margin-right: 0;
            }
        `,
    },
    title: {
        variant: "h6",
        weight: headingsFontConfig.weight.extraBold,
        color: "common.ui8",
        css: css`
            letter-spacing: 0;
            display: block;
            max-width: 104px;
            text-transform: capitalize;
            margin-bottom: 0;

            ${xScreen("lte", 1150)} {
                ${hideElement}
            }
        `,
    },
    arrowIcon: {
        size: 10,
        css: css`
            margin-left: 8px;
            margin-top: 4px;
            margin-right: 0;
        `,
    },
};

const HeaderClickable: FC<Props> = ({
    iconProps,
    titleProps,
    title,
    titleBreakWords,
    extendedStyles,
    children,
    isNewTabletScreen = false,
    ...otherProps
}) => {
    const { isTabletScreen, isMobileScreen } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
    }));

    const classNames = [isTabletScreen || isNewTabletScreen ? "mobile-mode" : ""].join(" ").trim();
    const [styles] = useState(() => mergeWithCss(headerClickableStyles, extendedStyles));

    return (
        <Clickable {...styles.clickable} {...otherProps} className={classNames} aria-label={title}>
            {children !== 0 && children}
            {(!children || children === 0) && iconProps && (
                <Icon {...styles.icon} color="primary" size={isMobileScreen ? 24 : 16} {...iconProps} />
            )}
            <Typography {...styles.title} {...titleProps} ellipsis={!titleBreakWords} className="clickable-title">
                {title}
            </Typography>
            <StyledIcon className="arrow" src={AngleDown} {...styles.arrowIcon} />
        </Clickable>
    );
};

export default HeaderClickable;
